import React, { Component } from 'react';
import { Route, Switch, matchPath, Redirect } from 'react-router-dom';
import { t } from 'i18next';
import Reduxify from '../../services/ReduxifyController';
import asyncComponent from '../../components/AsyncComponent';
import tbkApi from '../../services/tbkApi';
import { SW_NEW_CONTENT } from '../../registerServiceWorker';
import { propTypes, defaultProps, mapStateToProps, mapDispatchToProps } from './App.props';
import { fetchLocations, fetchPromotions} from './App.functions';
import { Header } from '../Header';
import { Preview } from '../../components/Preview';
import { NotFound } from './App.components';
import {SET_APP_USER, SET_APP_DATA} from './App.actions';
import dispatchCustomEvent from '../../services/CustomEvents';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Library = asyncComponent(() => import('../Library'));
const Manager = asyncComponent(() => import('../Manager'));
const Login = asyncComponent(() => import('../Login'));
const Home = asyncComponent(() => import('../Home'));
const Editor = asyncComponent(() => import('../Editor'));

toast.configure({
    position: 'top-center',
})

class App extends Component {

    static propTypes = propTypes;
    static defaultProps = defaultProps;
    static mapStateToProps = mapStateToProps;
    static mapDispatchToProps = mapDispatchToProps;

    constructor(props) {
        super(props);
        if ( ! this.isPreviewOnly() ) {
            tbkApi.prepare().then(
                () => {
                    tbkApi.getAppData().then( data => {
                        this.props.dispatch({
                            type: SET_APP_DATA,
                            data: data
                        })
                    });
                    tbkApi.getUser().then( user => {
                        this.props.dispatch({
                            type: SET_APP_USER,
                            user: user,
                        });
                    }).catch( e => {
                        this.props.dispatch({
                            type: SET_APP_USER,
                            user: {isGuest: true},
                        })
                    });
                }
            );
        }
        this.handleNewContent = this.handleNewContent.bind(this);
    }

    /**
     * If we're only previewing, we can be a bit more efficient by not getting the user, the locations and the promotions.
     * Rather, we'll just setup the app to dispatch a 'previewReady' event to the top-level window when we're loaded.
     * This is all so that the iframed preview boxes can a) load quickly and b) show an actual preview before user has
     * made changes to the form.
     *
     * @returns {boolean}
     */
    isPreviewOnly() {
        return !!matchPath ( this.props.location.pathname, '/preview/');
    }

    componentDidMount() {
        document.addEventListener( SW_NEW_CONTENT, this.handleNewContent );

        if ( this.isPreviewOnly() ) {
            dispatchCustomEvent( 'previewReady', window.top, {detail: window.document} );
        }
    }

    componentWillUnmount() {
        document.removeEventListener( SW_NEW_CONTENT, this.handleNewContent );
    }

    handleNewContent() {
        if ( ! this.isPreviewOnly() && window.confirm( t('There is a new version of the app is available.  Refresh now to see those changes?') ) ) {
            window.location.reload();
        }
    }

    componentDidUpdate(prevProps) {
        if ( !!this.props.locations || !!this.props.promotions ) {
            // already got 'em.
            return;
        }
        if ( ( ! prevProps.user || prevProps.user.isGuest ) && this.props.user && ! this.props.user.isGuest ) {
            this.props.dispatch(fetchLocations());
            this.props.dispatch(fetchPromotions());
        }
    }

    render() {
        const { user, data } = this.props;

        if ( user && data ) {
            if ( user.isGuest ) {
                /**
                 * We don't have a logged-in user.  If we're not on the login page, then redirect there, otherwise,
                 * render the login page
                 */
                if ( '/login' === this.props.location.pathname ) {
                    return <div className="app">
                        <Header/>
                        <main id="main-content">
                            <Login />
                        </main>
                    </div>
                } else {
                    let to = {
                        pathname: '/login'
                    };
                    if ( this.props.location.pathname !== '/' ) {
                        to.search = '?redirect=' + this.props.location.pathname;
                    }
                    return <Redirect to={to} />
                }
            } else if ( '/login' === this.props.location.pathname ) {
                return <Redirect to="/"/>;
            } else if ( '/logout' === this.props.location.pathname ) {
                setTimeout(() => {
                    this.props.dispatch({
                        type: SET_APP_USER,
                        user: {isGuest: true},
                    });
                    tbkApi.postLogout();
                });
                return <Redirect to="/login" />
            } else {
                const previewPath = matchPath ( this.props.location.pathname, '/preview/');

                return (
                    <div className="app">
                        {!previewPath && <Header user={this.props.user}/> }

                        <main id="main-content" ref={this.mainContentRef}>

                            {(this.props.promotions && this.props.locations)  ?
                                <Switch>
                                    <Route exact path="/home" component={Home} />
                                    <Route exact path="/" component={Home} />
                                    <Route path="/manager/:slug" component={Manager} />
                                    <Route path="/manager" component={Manager} />
                                    <Route path="/library" component={Library} />
                                    <Route path="/editor/:slug/:type(coupon|event|alert)" component={Editor} />
                                    <Route path="/editor/:slug/:design_option(custom|plug_n_play)" component={Editor} />
                                    <Route path="/editor/:slug/:layout" component={Editor} />
                                    <Route path="/editor/:slug" component={Editor} />
                                    <Route path="/editor" component={Editor} />
                                    <Route path="/preview" component={Preview} />
                                    <Route component={NotFound} />
                                </Switch>
                                : <div className="jiffy--loading-message"><div><i className="icon icon-cached icomoon-spin"></i><br/>{t('LOADING')}</div></div>}
                        </main>
                    </div>
                )
            }
        } else if ( this.isPreviewOnly() ) {
            return <div className="app">
                <main id="main-content" ref={this.mainContentRef}>
                    <Switch>
                        <Route path="/preview" component={Preview} />
                    </Switch>
                </main>
            </div>;
        } else {
            /**
             * We don't yet know if we have an authenticated user.  Show the loading mask
             */
            return (
                <div className="app">
                   <div className="jiffy--loading-message"><div><i className="icon icon-cached icomoon-spin"></i><br/>{t('LOADING')}</div></div>
                </div>
            )
        }
    }
}

export default Reduxify( App );
